import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import {
  MdsQuickPhrasesRound,
  MdsSaveRound,
} from "react-icons-with-materialsymbols/mds";
import { useParams } from "react-router-dom";
import { useReactFlow } from "reactflow";

import { ToastType, useShowToast } from "@/components/toast";
import {
  toggleLLMConfigPanel,
  useLazyGetWorkflowQuery,
} from "@/features/workflow-studio";
import { useWorkflowActions } from "@/features/workflow-studio/hooks/useWorkflowActions";
import {
  getEditingAllowed,
  getNodeStatus,
  showLLMConfigPanel,
  triggerRun as triggerRunAction,
} from "@/features/workflow-studio/redux";
import { useAppDispatch, useAppSelector } from "@/reduxHooks";

import { useSaveAndExecuteMutation, useSaveVersionMutation } from "../../api";
import {
  currentActiveVersion,
  getActivePlayground,
  getCurrentRunResults,
  getInputPrompt,
  isExecuteEnabled,
  isLLMEditable,
  selectResultRowCount,
  showPromptHistory,
} from "../../redux";
import { useLLMValidation } from "../../utils/llm-validation";
import { convertTextGenVersionToSaveRunPayload } from "../../utils/transform-payload";

import LLMCostEstimationCard from "./llm-cost-estimation";

export const LLMConfigHeader = () => {
  const [saveVersion, { isLoading }] = useSaveVersionMutation();
  const [_, { isLoading: isExecuting }] = useSaveAndExecuteMutation();

  const { getNode } = useReactFlow();
  const toast = useShowToast();
  const isValid = useLLMValidation();
  const { fetchAndUpdateWorkflow } = useWorkflowActions();
  const dispatch = useAppDispatch();

  const [triggerRun, setTriggerRun] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const activeVersion = useAppSelector(currentActiveVersion);
  const currentPlayground = useAppSelector(getActivePlayground);
  const currentRunResults = useAppSelector(getCurrentRunResults);
  const inputSampleCount = useAppSelector(selectResultRowCount);
  const isHistoryOpen = useAppSelector(showPromptHistory);
  const inputPrompt = useAppSelector(getInputPrompt);
  const executeEnabled = useAppSelector(isExecuteEnabled);
  const isLLMEditDisabled = useAppSelector(isLLMEditable);

  const isWFEditAllowed = useAppSelector(getEditingAllowed);
  const llmPanel = useAppSelector(showLLMConfigPanel);

  const currentNode = getNode(llmPanel.nodeId!);

  const isNodeLocked = useAppSelector(getNodeStatus(llmPanel.nodeId!))
    ?.isLocked;

  const isDisabled =
    isLLMEditDisabled ||
    isLoading ||
    !currentPlayground ||
    isEmpty(inputPrompt) ||
    isNodeLocked;
  const isExecuteLoading = isLoading || isExecuting || isClosing;

  const isExecuteDisabled = useMemo(() => {
    if (!executeEnabled || isLLMEditDisabled) return true;
    if (!isValid) return true;
    if (!currentRunResults || isEmpty(currentRunResults.headers)) return true;
    return isLoading || !activeVersion;
  }, [
    isLoading,
    activeVersion,
    currentRunResults,
    isValid,
    isLLMEditDisabled,
    executeEnabled,
  ]);

  useEffect(() => {
    if (!triggerRun) return;
    if (triggerRun) {
      dispatch(triggerRunAction(true));
      setTimeout(() => dispatch(triggerRunAction(false)), 1000);
      setTriggerRun(false);
      dispatch(toggleLLMConfigPanel({ show: false }));
    }
  }, [triggerRun]);

  const saveLLMNode = async () => {
    if (!activeVersion) return;
    if (!isDisabled) {
      await saveVersion({
        playgroundId: currentPlayground.id,
        textGenId: currentPlayground.textGeneration[0].id,
        body: convertTextGenVersionToSaveRunPayload(
          activeVersion,
          inputSampleCount ?? 0
        ),
      });
    }
  };

  const onSave = async () => {
    try {
      await saveLLMNode();
      await fetchAndUpdateWorkflow();
      toast({
        description: "Saved Successfully",
        status: ToastType.Success,
      });
    } catch (error) {
      toast({
        description: "Failed to Save",
        status: ToastType.Error,
      });
    }
  };

  const onLLMClose = async () => {
    try {
      setIsClosing(true);
      if (!isNodeLocked) {
        await fetchAndUpdateWorkflow();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsClosing(false);
      dispatch(toggleLLMConfigPanel({ show: false }));
    }
  };

  const onSaveAndExecute = async () => {
    try {
      await saveLLMNode();
      await fetchAndUpdateWorkflow();
      toast({
        description: "Saved Successfully",
        status: ToastType.Success,
      });
      setTriggerRun(true);
    } catch (error) {
      toast({
        description: "Failed to Save & Execute",
        status: ToastType.Error,
      });
    }
  };

  return (
    <Flex className={clsx("w-full border-t px-3 py-1 justify-between")}>
      <Flex className="gap-2 items-center">
        {!isHistoryOpen && (
          <Button
            colorScheme="dark"
            isDisabled={isClosing}
            isLoading={isClosing}
            leftIcon={<MdArrowBack />}
            onClick={onLLMClose}
            size={"sm"}
            variant={"ghost"}
          >
            Back
          </Button>
        )}
        <Flex className="gap-2 items-center">
          <Box
            className={clsx(
              "grid place-items-center h-8 w-8 rounded",
              "shrink-0 text-brand-500 bg-brand-50"
            )}
          >
            <Icon
              className="stroke-[22]"
              as={MdsQuickPhrasesRound}
              fontSize={20}
            />
          </Box>
          <Box className="text-sm font-medium">{currentNode?.data.displayName}</Box>
        </Flex>
      </Flex>
      <Flex gap={6}>
        <LLMCostEstimationCard />
        {!isHistoryOpen && isWFEditAllowed && (
          <Flex gap={2}>
            <Button
              colorScheme="dark"
              isDisabled={isDisabled}
              isLoading={isLoading}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              leftIcon={<MdsSaveRound className="stroke-[22]" />}
              onClick={onSave}
              size={"sm"}
            >
              Save
            </Button>
            {/* <Tooltip
              className="w-36 h-14 flex items-center !bg-gray-800 text-white text-center"
              sx={{
                ".chakra-tooltip__arrow": {
                  backgroundColor: "rgb(64,64,64) !important",
                },
              }}
              hasArrow
              isDisabled={!isExecuteDisabled}
              label={"Run Test required before execution"}
              placement="top-end"
              shouldWrapChildren={true}
            > */}
            {/* <Button
                colorScheme="dark"
                isDisabled={isExecuteDisabled || isExecuteLoading}
                isLoading={isExecuteLoading}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={onSaveAndExecute}
                size={"sm"}
                variant={"solid"}
              >
                Save & Execute
              </Button> 
            </Tooltip>*/}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
